import { runtime } from "../core/runtime.js";
import { t } from "../core/i18n.js";
import { getSyncInfo } from "../services/storage.js";
import {
  plansIn,
  mutablePlans,
  savePlans,
  preservePlanHistory,
} from "../core/planning.js";
import { saveJSON } from "../core/storage.js";
import { KEYS } from "../../shared/schema.js";

const drafts = new Map();
let workspace;
let fieldObserver;

function currentDrafts() {
  const current = getSyncInfo().session?.workspaceId;
  if (workspace !== current) {
    drafts.clear();
    workspace = current;
  }
  for (const [group, tasks] of drafts) {
    const existing = plansIn(group);
    for (const id of tasks.keys()) {
      if (!existing.some((task) => task.id === id)) tasks.delete(id);
    }
    if (!tasks.size) drafts.delete(group);
  }
  return drafts;
}

export function inlinePlan(task, group) {
  return { ...task, ...currentDrafts().get(group)?.get(task.id) };
}

export function hasPlanEdits() {
  return currentDrafts().size > 0;
}

function resizeField(field) {
  if (field.tagName !== "TEXTAREA") return;
  field.style.height = "auto";
  field.style.height = field.scrollHeight + 2 + "px";
}

export function bindPlanEdits(onSave) {
  fieldObserver?.disconnect();
  const fields = document.querySelectorAll("[data-inline-plan]");
  const widths = new WeakMap();
  fieldObserver = new ResizeObserver((entries) => {
    for (const { target, contentRect } of entries) {
      if (widths.get(target) === contentRect.width) continue;
      widths.set(target, contentRect.width);
      target.querySelectorAll("textarea").forEach(resizeField);
    }
  });
  document
    .querySelectorAll(".plan-manager-task")
    .forEach((task) => fieldObserver.observe(task));
  fields.forEach((field) => {
    resizeField(field);
    field.addEventListener("input", () => {
      field.setCustomValidity("");
      resizeField(field);
      const group = runtime.manageDay,
        id = field.dataset.inlinePlan,
        key = field.dataset.inlineField,
        task = plansIn(group).find((item) => item.id === id);
      if (!task) return;
      currentDrafts();
      const tasks = drafts.get(group) || new Map(),
        changes = tasks.get(id) || {};
      if (field.value === (task[key] || "")) delete changes[key];
      else changes[key] = field.value;
      if (Object.keys(changes).length) tasks.set(id, changes);
      else tasks.delete(id);
      if (tasks.size) drafts.set(group, tasks);
      else drafts.delete(group);
      document.querySelector("[data-plan-save-bar]").hidden = !hasPlanEdits();
    });
  });
  document
    .querySelector("[data-save-manager]")
    ?.addEventListener("click", onSave);
}

export function savePlanEdits() {
  currentDrafts();
  for (const [group, tasks] of drafts) {
    for (const [id, changes] of tasks) {
      if (changes.name !== undefined && !changes.name.trim())
        return { group, id };
    }
  }
  preservePlanHistory();
  for (const [group, tasks] of drafts) {
    const existing = mutablePlans(group);
    for (const [id, changes] of tasks) {
      const task = existing.find((item) => item.id === id);
      if (task) {
        for (const [key, value] of Object.entries(changes))
          task[key] = value.trim();
      }
    }
    savePlans(group);
  }
  saveJSON(KEYS.custom, runtime.state.custom);
  drafts.clear();
  return null;
}

export function showPlanEditError(id) {
  const field = document.querySelector(
    `[data-inline-plan="${CSS.escape(id)}"][data-inline-field="name"]`,
  );
  if (!field) return;
  field.setCustomValidity(t("Please enter a plan name."));
  field.focus();
  field.reportValidity();
}

window.addEventListener("beforeunload", (event) => {
  if (!hasPlanEdits()) return;
  event.preventDefault();
  event.returnValue = "";
});
